<template>
  <div class="card m-3 text-center">
    <h4 class="card-header">{{ t('reset-password') }}</h4>

    <div class="d-flex card-body justify-content-center">
      <div class="reset-form">
        <!-- Toggle Email/SMS — pokazywany tylko jeśli SMS-reset jest włączony przez feature flag.
             Gdy SMS off, widok zachowuje się dokładnie jak wcześniej (czyste pole email). -->
        <div v-if="smsResetEnabled" class="channel-tabs btn-group w-100" role="tablist">
          <button
            type="button"
            class="btn"
            :class="channel === 'email' ? 'btn-secondary' : 'btn-outline-secondary'"
            @click="setChannel('email')"
          >
            {{ t('reset-via-email') }}
          </button>
          <button
            type="button"
            class="btn"
            :class="channel === 'sms' ? 'btn-secondary' : 'btn-outline-secondary'"
            @click="setChannel('sms')"
          >
            {{ t('reset-via-sms') }}
          </button>
        </div>

        <!-- Wariant EMAIL (domyślny / istniejący) -->
        <Form
          v-if="channel === 'email'"
          @submit="onSubmitEmail"
          :validation-schema="emailSchema"
          v-slot="{ errors, isSubmitting }"
        >
          <div class="form-group row mb-1">
            <label class="col-sm-2 col-form-label">{{ t('email') }}</label>
            <div class="col-sm-10">
              <Field
                name="email"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.email }"
              />
              <div class="invalid-feedback">{{ errors.email }}</div>
            </div>
          </div>

          <div class="form-group row d-flex flex-column align-items-center">
            <div class="col-auto hint">
              {{ t('type-your-email-send-reset-token') }}
            </div>
            <div class="col-auto">
              <button class="btn btn-outline-secondary mt-3" :disabled="isSubmitting">
                <span v-show="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                {{ t('send-email') }}
              </button>
            </div>
          </div>
        </Form>

        <!-- Wariant SMS — prefix kierunkowy (3 znaki, np. "+48") + 9 cyfr -->
        <Form
          v-else
          @submit="onSubmitSms"
          :validation-schema="smsSchema"
          :initial-values="{ phoneNumberDir: '+48', phoneNumber: '' }"
          v-slot="{ errors, isSubmitting }"
        >
          <div class="form-group row mb-1 phone-row">
            <label class="col-sm-2 col-form-label">{{ t('phone-number') }}</label>
            <div class="col-sm-3 pe-0">
              <Field
                name="phoneNumberDir"
                type="text"
                maxlength="3"
                class="form-control"
                :class="{ 'is-invalid': errors.phoneNumberDir }"
              />
              <div class="invalid-feedback">{{ errors.phoneNumberDir }}</div>
            </div>
            <div class="col-sm-7 ps-1">
              <Field
                name="phoneNumber"
                type="text"
                inputmode="numeric"
                maxlength="9"
                class="form-control"
                :class="{ 'is-invalid': errors.phoneNumber }"
              />
              <div class="invalid-feedback">{{ errors.phoneNumber }}</div>
            </div>
          </div>

          <div class="form-group row d-flex flex-column align-items-center">
            <div class="col-auto hint">
              {{ t('type-your-phone-send-sms-code') }}
            </div>
            <div class="col-auto">
              <button class="btn btn-outline-secondary mt-3" :disabled="isSubmitting">
                <span v-show="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                {{ t('send-sms-code') }}
              </button>
            </div>
          </div>
        </Form>

        <div class="form-status" aria-live="polite">
          <div
            v-if="statusMessage && !statusDismissed"
            class="alert alert-dismissible fade show"
            :class="statusType === 'success' ? 'alert-success' : 'alert-danger'"
            role="alert"
          >
            {{ statusMessage }}
            <button
              type="button"
              class="btn-close"
              aria-label="Zamknij"
              @click="statusDismissed = true"
            ></button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { Form, Field } from 'vee-validate';
import * as Yup from 'yup';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();

// Feature flag z env. Frontend i backend muszą być uzgodnione — gdy backend ma SMS off,
// front też powinien (odpytanie endpointu SMS i tak zwróci 204, ale UI jest mylące).
const smsResetEnabled = String(process.env.VUE_APP_FEATURE_SMS_RESET_ENABLED).toLowerCase() === 'true';

const channel = ref('email');
const statusMessage = ref('');
const statusType = ref('error');
const statusDismissed = ref(false);

const emailSchema = Yup.object({
  email: Yup.string()
    .email(t('incorrect-email-address'))
    .required(t('user-email-required')),
});

const smsSchema = Yup.object({
  phoneNumberDir: Yup.string()
    .matches(/^\+\d{2}$/, t('phone-prefix-invalid'))
    .required(t('phone-prefix-required')),
  phoneNumber: Yup.string()
    .matches(/^\d{9}$/, t('phone-number-invalid'))
    .required(t('phone-number-required')),
});

function setChannel(c) {
  channel.value = c;
  statusMessage.value = '';
  statusDismissed.value = false;
}

function showStatus(message, type = 'error') {
  statusMessage.value = message;
  statusType.value = type;
  statusDismissed.value = false;
}

async function onSubmitEmail(values) {
  statusMessage.value = '';
  statusDismissed.value = false;
  try {
    await authStore.resetPassword(values.email);
    showStatus(t('reset-email-sent-info'), 'success');
  } catch (error) {
    showStatus(error?.message || t('reset-email-send-failed'), 'error');
  }
}

async function onSubmitSms(values) {
  statusMessage.value = '';
  statusDismissed.value = false;
  try {
    await authStore.requestSmsResetCode(values.phoneNumberDir, values.phoneNumber);
    // BE zawsze 204; jeśli numer nie istnieje, w UI nie zdradzamy tego — przechodzimy
    // do widoku weryfikacji tak czy inaczej. Gdyby numer nie był zarejestrowany, user
    // po prostu nie dostanie SMS-a i kod nigdy nie zadziała.
    router.push('/reset-password/code');
  } catch (error) {
    showStatus(error?.message || t('sms-code-send-failed'), 'error');
  }
}
</script>

<style scoped>
.card-body {
  background: #f5f4f3;
}
.reset-form {
  width: 460px;
  max-width: 100%;
}
.channel-tabs {
  margin-bottom: 1.25rem;
}
.channel-tabs .btn {
  border-radius: 0;
  font-size: 13px;
  font-weight: bold;
  padding: 6px 12px;
}
.col-form-label {
  text-align: right;
  line-height: 25px;
}
.form-group {
  font-size: 12px;
}
.hint {
  margin-top: 0.5rem;
}
.phone-row .form-control {
  padding: 10px;
}
button:not(.btn-close) {
  border: 0;
  color: #231f20;
  font-size: 15px;
  font-weight: bold;
}
.btn-outline-secondary {
  border: 1px solid grey;
}
input.form-control {
  border: 0;
  border-left: 1px solid #c8c7c7;
  border-top: 1px solid #c8c7c7;
  padding: 10px;
}
input.form-control:focus {
  border-color: #c8c7c7;
  box-shadow: 0 0 0 0.25rem rgba(100, 101, 104, 0.25);
}
.form-status {
  min-height: 56px;
  margin-top: 0.75rem;
}
.form-status .alert {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}
.form-status .alert-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
}
</style>
